import type { FC } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import ListCard from '@wix/da-ds/pkg/List/ListCard';
import BatchActionItem from './_partials/BatchActionItem';
import { BatchAction, StudioSection } from '../../types/store';
import { useEnabledFlagList } from '@wix/da-react-context/pkg/flags/hooks';

import s from './MobileBatchEdit.scss';

export interface Props {
  activeSection: StudioSection;
  selectedCount: number;
  selectedBatchAction?: BatchAction;
  selectBatchAction: (batchAction: BatchAction) => void;
  cancelBatchAction: () => void;
  requestClose: () => void;
}

const BATCH_ACTION_ITEMS = [
  { action: BatchAction.EDIT_TITLE, excludeSection: [StudioSection.JOURNALS] },
  {
    action: BatchAction.EDIT_TAGS,
    excludeSection: [
      StudioSection.JOURNALS,
      StudioSection.APPS,
      StudioSection.APPS_UNLISTED,
    ],
  },
  {
    action: BatchAction.EDIT_DESCRIPTION,
    excludeSection: [StudioSection.JOURNALS],
  },
  {
    action: BatchAction.EDIT_GALLERY,
    excludeSection: [StudioSection.JOURNALS],
  },
  {
    action: BatchAction.EDIT_TIER,
    withCoreSymbol: true,
    excludeSection: [StudioSection.APPS, StudioSection.APPS_UNLISTED],
  },
  {
    action: BatchAction.EDIT_SCHEDULE,
    section: [
      StudioSection.DRAFTS,
      StudioSection.LITERATURE,
      StudioSection.JOURNALS,
      StudioSection.SCHEDULED,
      StudioSection.STASH,
      StudioSection.PLAYGROUND,
      StudioSection.PRODUCTS_PCP_UNLISTED,
      StudioSection.PRODUCTS_EXCLUSIVE_UNLISTED,
    ],
    withCoreSymbol: true,
  },
  {
    action: BatchAction.EDIT_DISPLAY_OPTIONS,
    excludeSection: [
      StudioSection.JOURNALS,
      StudioSection.APPS,
      StudioSection.APPS_UNLISTED,
    ],
  },
  {
    action: BatchAction.EDIT_LICENSE,
    excludeSection: [
      StudioSection.JOURNALS,
      StudioSection.APPS,
      StudioSection.APPS_UNLISTED,
    ],
  },
  {
    action: BatchAction.EDIT_COMMENTS,
    excludeSection: [StudioSection.JOURNALS, StudioSection.APPS],
  },
  {
    action: BatchAction.EDIT_LABELS,
    excludeSection: [
      StudioSection.JOURNALS,
      StudioSection.APPS,
      StudioSection.APPS_UNLISTED,
    ],
  },
  {
    action: BatchAction.EDIT_PRESET,
    section: [
      StudioSection.DRAFTS,
      StudioSection.LITERATURE,
      StudioSection.SCHEDULED,
      StudioSection.STASH,
      StudioSection.PLAYGROUND,
    ],
  },
];

const MobileBatchEdit: FC<Props> = ({
  activeSection,
  selectedCount,
  selectedBatchAction,
  selectBatchAction,
  cancelBatchAction,
  requestClose,
}) => {
  const { t } = useTranslation();
  const enabledFlags = useEnabledFlagList();
  const filterBySection = useCallback(
    item =>
      (!item.section || item.section.includes(activeSection)) &&
      (!item.excludeSection || !item.excludeSection.includes(activeSection)) &&
      (!item.feature || enabledFlags.includes(item.feature)) &&
      (!item.feature_exclude || !enabledFlags.includes(item.feature_exclude)),
    [activeSection, enabledFlags]
  );

  return (
    <Modal
      className={s['wrapper']}
      layoutClassName={s['layout']}
      contentClassName={s['content']}
      heading={t('studio.modals.batchEdit.title', { count: selectedCount })}
      onRequestClose={() => {
        cancelBatchAction();
        requestClose();
      }}
      showHeaderBorder
      showFooterBorder
      shouldCloseOnOverlayClick
      isOpen
      isFullscreen
      withScrollableContentWrapper
    >
      <ListCard className={s['list-container']}>
        {BATCH_ACTION_ITEMS.filter(filterBySection).map(
          ({ action, withCoreSymbol }) => (
            <BatchActionItem
              key={`action-${action}`}
              action={action}
              label={t(`studio.modals.batchEdit.actions.${action}`)}
              isExpanded={selectedBatchAction === action}
              onExpand={expand =>
                expand ? selectBatchAction(action) : cancelBatchAction()
              }
              withCoreSymbol={withCoreSymbol}
            />
          )
        )}
      </ListCard>
    </Modal>
  );
};
MobileBatchEdit.displayName = 'MobileBatchEdit';

export default MobileBatchEdit;
